import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/LoadingScreen';
import { ApiRequestError } from '@/services/api';
import { operationalAccessService } from '@/services/operational.service';
import {
  ACCESS_TITLES,
  type AccessLevel,
  type OperationalModule,
} from '@/types/operational';
import { v } from '@/theme/tokens';

/**
 * «Дополнительный доступ» — owner-only, and the server says so.
 *
 * This dialog is offered only where the owner already is (Настройки →
 * Пользователи и роли), but that is presentation. What actually prevents
 * anybody else from granting a module is `require_owner_actor` on the API,
 * which checks the *role code* rather than a permission — so no future edit
 * to the permission matrix can widen it.
 *
 * For the owner as a **target** the controls are shown fixed at «Управление»
 * and disabled, with the reason spelled out: an owner has full operational
 * access always, and there is no request that can take it away.
 */
export function ModuleAccessDialog({
  userId,
  userName,
  onClose,
}: {
  userId: string;
  userName: string;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const [draft, setDraft] = useState<Record<string, AccessLevel>>({});
  const [error, setError] = useState<string | null>(null);

  const grants = useQuery({
    queryKey: ['operational', 'access', 'user', userId],
    queryFn: ({ signal }) => operationalAccessService.read(userId, signal),
  });

  useEffect(() => {
    if (!grants.data) return;
    setDraft(
      Object.fromEntries(grants.data.grants.map((grant) => [grant.module, grant.access])),
    );
  }, [grants.data]);

  const save = useMutation({
    mutationFn: () =>
      operationalAccessService.set(
        userId,
        Object.entries(draft).map(([module, access]) => ({
          module: module as OperationalModule,
          access,
        })),
      ),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['operational'] });
      void client.invalidateQueries({ queryKey: ['audit'] });
      onClose();
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiRequestError ? e.message : 'Не удалось сохранить доступ'),
  });

  const isOwner = grants.data?.is_owner ?? false;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Дополнительный доступ"
      className="rt-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        zIndex: 60,
      }}
      onClick={onClose}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="rt-dialog"
        style={{
          borderRadius: 16,
          padding: 20,
          width: 480,
          maxWidth: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
          Дополнительный доступ
        </div>
        <div style={{ fontSize: 12.5, color: v('ink-muted'), marginBottom: 14 }}>{userName}</div>

        {grants.isPending && <Spinner />}

        {isOwner && (
          <div
            style={{
              background: v('accent-soft'),
              color: v('accent-ink'),
              padding: '9px 12px',
              borderRadius: 10,
              fontSize: 12.5,
              marginBottom: 14,
            }}
          >
            У владельца всегда полный доступ ко всем операционным модулям. Отключить его нельзя —
            иначе владелец мог бы случайно закрыть себе доступ к собственной системе.
          </div>
        )}

        {error && (
          <div
            role="alert"
            style={{
              background: v('negative-soft'),
              color: v('negative'),
              padding: '9px 12px',
              borderRadius: 10,
              fontSize: 12.5,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}

        {(grants.data?.grants ?? []).map((grant) => (
          <div key={grant.module} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 6 }}>{grant.title}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {grant.available.map((level) => {
                const active = (draft[grant.module] ?? grant.access) === level;
                return (
                  <label
                    key={level}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 11px',
                      borderRadius: 999,
                      fontSize: 12.5,
                      fontWeight: active ? 700 : 500,
                      cursor: isOwner ? 'not-allowed' : 'pointer',
                      background: active ? v('accent-soft') : v('panel'),
                      color: active ? v('accent-ink') : v('ink-sub'),
                      border: `1px solid ${active ? v('accent-soft') : v('line')}`,
                      opacity: isOwner ? 0.6 : 1,
                    }}
                  >
                    <input
                      type="radio"
                      name={`module-${grant.module}`}
                      aria-label={`${grant.title}: ${ACCESS_TITLES[level]}`}
                      checked={active}
                      disabled={isOwner}
                      onChange={() =>
                        setDraft((current) => ({ ...current, [grant.module]: level }))
                      }
                      style={{ margin: 0 }}
                    />
                    {ACCESS_TITLES[level]}
                  </label>
                );
              })}
            </div>
          </div>
        ))}

        <p style={{ fontSize: 11.5, color: v('ink-faint'), marginTop: 4, marginBottom: 16 }}>
          Дополнительный доступ не расширяет область объектов. Пользователь с доступом к
          инвентаризации видит только те рестораны, которые уже входят в его область доступа.
          Каждое изменение записывается в журнал аудита.
        </p>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="primary"
            disabled={isOwner || save.isPending || grants.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Сохранение…' : 'Сохранить'}
          </Button>
          <Button onClick={onClose}>{isOwner ? 'Закрыть' : 'Отмена'}</Button>
        </div>
      </div>
    </div>
  );
}
